import { useEffect, useState } from 'react';
import {
  deleteLeadAppointment,
  getLeadAppointments,
  sendLeadAppointmentConfirmation,
  sendLeadAppointmentReminder,
  updateLeadAppointment,
} from "../services/api";
import { formatDate } from '../utils';
import { appointmentHelp } from '../utils/helpContent';
import HelpTooltip from './HelpTooltip';

const LeadAppointmentList = ({ authToken, leadId, refreshTrigger = 0, compact = false, showFirstOnly = false, onCountChange }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const statusColors = {
    scheduled: 'bg-accent-teal-subtle text-blue-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-accent-teal-subtle text-gray-800',
    no_show: 'bg-red-100 text-red-800',
    cancelled: 'bg-yellow-100 text-yellow-800',
    rescheduled: 'bg-purple-100 text-purple-800'
  };

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'no_show', label: 'No Show' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rescheduled', label: 'Rescheduled' }
  ];

  useEffect(() => {
    fetchAppointments();
  }, [leadId, authToken, refreshTrigger]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      console.log('Fetching appointments for lead:', leadId);
      const data = await getLeadAppointments(authToken, leadId);
      console.log('Appointments API response:', data);
      
      // Handle both array and object responses (some APIs return {results: []})
      const appointmentsArray = Array.isArray(data) ? data : (data?.results || data?.appointments || []);
      console.log('Processed appointments:', appointmentsArray);
      
      setAppointments(appointmentsArray);
      setError(null);
      
      // Update parent component with count
      if (onCountChange) {
        onCountChange(appointmentsArray.length);
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      console.error('Error details:', err.response?.data);
      setError(`Failed to load appointments: ${err.response?.data?.detail || err.message}`);
      setAppointments([]);
      
      // Update parent component with count
      if (onCountChange) {
        onCountChange(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      setActionLoading(prev => ({ ...prev, [`status-${appointmentId}`]: true }));
      
      await updateLeadAppointment(authToken, leadId, appointmentId, {
        status: newStatus
      });
      
      // Update local state
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, status: newStatus } : apt
      ));
    } catch (err) {
      console.error('Error updating appointment status:', err);
      setError('Failed to update appointment status');
    } finally {
      setActionLoading(prev => ({ ...prev, [`status-${appointmentId}`]: false }));
    }
  };

  const handleSendReminder = async (appointmentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`reminder-${appointmentId}`]: true }));
      
      await sendLeadAppointmentReminder(authToken, leadId, appointmentId);
      
      // Update local state to show reminder was sent
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, reminder_sent: true } : apt
      ));
      
      // Show success message briefly
      setTimeout(() => {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId ? { ...apt, showReminderSuccess: true } : apt
        ));
        setTimeout(() => {
          setAppointments(prev => prev.map(apt => 
            apt.id === appointmentId ? { ...apt, showReminderSuccess: false } : apt
          ));
        }, 2000);
      }, 100);
      
    } catch (err) {
      console.error('Error sending reminder:', err);
      setError('Failed to send appointment reminder');
    } finally {
      setActionLoading(prev => ({ ...prev, [`reminder-${appointmentId}`]: false }));
    }
  };

  const handleSendConfirmation = async (appointmentId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`confirmation-${appointmentId}`]: true }));
      
      await sendLeadAppointmentConfirmation(authToken, leadId, appointmentId);
      
      // Update local state
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, confirmation_sent: true } : apt
      ));
      
      // Show success message briefly
      setTimeout(() => {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId ? { ...apt, showConfirmationSuccess: true } : apt
        ));
        setTimeout(() => {
          setAppointments(prev => prev.map(apt => 
            apt.id === appointmentId ? { ...apt, showConfirmationSuccess: false } : apt
          ));
        }, 2000);
      }, 100);
      
    } catch (err) {
      console.error('Error sending confirmation:', err);
      setError('Failed to send appointment confirmation');
    } finally {
      setActionLoading(prev => ({ ...prev, [`confirmation-${appointmentId}`]: false }));
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`delete-${appointmentId}`]: true }));
      
      await deleteLeadAppointment(authToken, leadId, appointmentId);
      
      // Remove from local state
      const updatedAppointments = appointments.filter(apt => apt.id !== appointmentId);
      setAppointments(updatedAppointments);
      
      // Update parent component with new count
      if (onCountChange) {
        onCountChange(updatedAppointments.length);
      }
      
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete-${appointmentId}`]: false }));
    }
  };

  const isUpcoming = (dateTime) => {
    return new Date(dateTime) > new Date();
  };

  const isPastDue = (dateTime) => {
    return new Date(dateTime) < new Date();
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const [hours, minutes] = duration.split(':');
    const h = parseInt(hours);
    const m = parseInt(minutes);
    
    if (h === 0) {
      return `${m}m`;
    } else if (m === 0) {
      return `${h}h`;
    } else {
      return `${h}h ${m}m`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="flex items-center py-3 text-gray-500">
        <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="text-sm">No appointments scheduled yet</span>
      </div>
    );
  }

  // Sort appointments by date_time in descending order (most recent first)
  const sortedAppointments = [...appointments].sort((a, b) => {
    const dateA = new Date(a.date_time);
    const dateB = new Date(b.date_time);
    return dateB - dateA; // Descending order
  });

  // Filter appointments based on showFirstOnly prop
  const displayAppointments = showFirstOnly ? sortedAppointments.slice(0, 1) : sortedAppointments;
  
  // Show message if no appointments when in showFirstOnly mode
  if (showFirstOnly && appointments.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No appointments yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {displayAppointments.map((appointment) => (
        <div
          key={appointment.id}
          className={`border rounded-lg ${
            isUpcoming(appointment.date_time)
              ? "border-teal-200 bg-accent-teal-light-bg"
              : "border-border bg-background"
          } ${compact ? "p-3" : "p-4"}`}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h4
                  className={`font-medium text-gray-900 ${
                    compact ? "text-sm" : "text-base"
                  }`}
                >
                  {appointment.type?.charAt(0).toUpperCase() +
                    appointment.type?.slice(1) || "Appointment"}
                </h4>
                <div className="flex items-center space-x-1">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      statusColors[appointment.status] ||
                      "bg-accent-teal-subtle text-gray-800"
                    }`}
                  >
                    {statusOptions.find((s) => s.value === appointment.status)
                      ?.label || appointment.status}
                  </span>
                  <HelpTooltip
                    title={appointmentHelp.appointmentStatuses.title}
                    content={appointmentHelp.appointmentStatuses.content}
                    size="md"
                    position="top-left"
                  />
                </div>
                {isPastDue(appointment.date_time) &&
                  appointment.status === "scheduled" && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Past Due
                    </span>
                  )}
              </div>

              <div
                className={`text-gray-600 space-y-1 ${
                  compact ? "text-xs" : "text-sm"
                }`}
              >
                <div className="flex items-center space-x-1">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>{formatDate(appointment.date_time, "full")}</span>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{formatDuration(appointment.duration)}</span>
                  </div>

                  {appointment.staff && (
                    <div className="flex items-center space-x-1">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span>
                        {appointment.staff.user?.first_name}{" "}
                        {appointment.staff.user?.last_name}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions Dropdown */}
            <div className="relative">
              <details className="relative">
                <summary className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-accent-teal-subtle cursor-pointer">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </summary>

                <div className="absolute right-0 mt-1 w-48 bg-background rounded-md shadow-sm z-50 border border-border">
                  <div className="py-1">
                    {/* Status Update */}
                    <div className="px-3 py-2 border-b border-gray-100">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
                        Status
                        <HelpTooltip
                          title={appointmentHelp.updateStatus.title}
                          content={appointmentHelp.updateStatus.content}
                          className="ml-1"
                          size="sm"
                          position="left"
                        />
                      </label>
                      <select
                        value={appointment.status}
                        onChange={(e) =>
                          handleStatusChange(appointment.id, e.target.value)
                        }
                        disabled={actionLoading[`status-${appointment.id}`]}
                        className="w-full text-xs border border-border rounded px-2 py-1 focus:ring-1 focus:ring-text-primary"
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Send Reminder */}
                    <div className="relative group">
                      <button
                        onClick={() => handleSendReminder(appointment.id)}
                        disabled={
                          actionLoading[`reminder-${appointment.id}`] ||
                          appointment.showReminderSuccess
                        }
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-accent-teal-subtle disabled:opacity-50 flex items-center space-x-2"
                      >
                        {actionLoading[`reminder-${appointment.id}`] ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-text-primary border-t-transparent rounded-full"></div>
                            <span>Sending...</span>
                          </>
                        ) : appointment.showReminderSuccess ? (
                          <>
                            <svg
                              className="h-4 w-4 text-green-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-green-600">Sent!</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <span>Send Reminder</span>
                            {appointment.reminder_sent && (
                              <span className="text-xs text-green-600">
                                (✓ Sent)
                              </span>
                            )}
                          </>
                        )}
                      </button>
                      <div className="absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <HelpTooltip
                          title={appointmentHelp.sendReminder.title}
                          content={appointmentHelp.sendReminder.content}
                          size="sm"
                          position="left"
                        />
                      </div>
                    </div>

                    {/* Send Confirmation */}
                    <div className="relative group">
                      <button
                        onClick={() => handleSendConfirmation(appointment.id)}
                        disabled={
                          actionLoading[`confirmation-${appointment.id}`] ||
                          appointment.showConfirmationSuccess
                        }
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-accent-teal-subtle disabled:opacity-50 flex items-center space-x-2"
                      >
                        {actionLoading[`confirmation-${appointment.id}`] ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-text-primary border-t-transparent rounded-full"></div>
                            <span>Sending...</span>
                          </>
                        ) : appointment.showConfirmationSuccess ? (
                          <>
                            <svg
                              className="h-4 w-4 text-green-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-green-600">Sent!</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <span>Send Confirmation</span>
                            {appointment.confirmation_sent && (
                              <span className="text-xs text-green-600">
                                (✓ Sent)
                              </span>
                            )}
                          </>
                        )}
                      </button>
                      <div className="absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <HelpTooltip
                          title={
                            appointmentHelp.sendAppointmentConfirmation.title
                          }
                          content={
                            appointmentHelp.sendAppointmentConfirmation.content
                          }
                          size="sm"
                          position="left"
                        />
                      </div>
                    </div>

                    {/* Delete */}
                    <div className="relative group">
                      <button
                        onClick={() => handleDeleteAppointment(appointment.id)}
                        disabled={actionLoading[`delete-${appointment.id}`]}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 border-t border-gray-100 flex items-center space-x-2"
                      >
                        {actionLoading[`delete-${appointment.id}`] ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                            <span>Deleting...</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-4 0a1 1 0 00-1 1v1h6V3a1 1 0 00-1-1h-4zm-3 4h10"
                              />
                            </svg>
                            <span>Delete</span>
                          </>
                        )}
                      </button>
                      <div className="absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <HelpTooltip
                          title={appointmentHelp.deleteAppointment.title}
                          content={appointmentHelp.deleteAppointment.content}
                          size="sm"
                          position="left"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LeadAppointmentList;